
  import {
    defineComponent,
    ref,
    onMounted,
    computed,
    onBeforeUnmount,
  } from 'vue';
  import { useRouter } from 'vue-router';
  import { setCurrentPageBreadcrumbs } from '@/core/helpers/breadcrumb';
  import { useI18n } from 'vue-i18n/index';
  import { useStore } from 'vuex';
  import { Actions } from '@/store/enums/StoreEnums';
  import { useAbility } from '@casl/vue';
  import Swal from 'sweetalert2/dist/sweetalert2.js';
  import Datatable from '@/components/kt-datatable/KTDatatable.vue';
  import { BannerAssetsObject } from '@/store/modules/Assets/BannerModule';
  import { parseDeeplink } from '@/core/helpers/deeplink';

  export default defineComponent({
    name: 'banners-listing',
    components: {
      Datatable,
    },
    async setup() {
      const { t, te } = useI18n();
      const store = useStore();
      const router = useRouter();
      const { can } = useAbility();
      const search = ref<string>('');
      const screen = ref<string | null>(null);

      const screenOptions = ref([
        { label: 'Fund', value: 'fund' },
        { label: 'Home Services', value: 'home-services' },
        { label: 'Auctions', value: 'auction' },
        { label: 'Ads List', value: 'ads-list' },
      ]);

      const translate = (text) => {
        if (te(text)) {
          return t(text);
        } else {
          return text;
        }
      };

      onMounted(() => {
        setCurrentPageBreadcrumbs(translate('bannerList'), [
          translate('assets'),
        ]);
      });

      onBeforeUnmount(() => {
        store.dispatch(Actions.RESET_BANNERS_STORE);
      });

      await store.dispatch(Actions.GET_BANNER_ASSETS);
      const bannersAssets = ref<BannerAssetsObject>(
        store.getters.bannerAssetsList
      );

      await store.dispatch(Actions.GET_BANNERS);
      const loading = ref(false);
      const tableHeader = ref([
        {
          name: translate('id'),
          key: 'id',
        },

        {
          name: translate('screen'),
          key: 'screen',
        },
        {
          name: translate('type'),
          key: 'typeList',
        },
        {
          name: translate('deeplinkLink'),
          key: 'deeplink',
        },
        {
          name: translate('active'),
          key: 'isActive',
        },

        {
          name: translate('actions'),
          key: 'actions',
        },
      ]);
      const deeplinkScreen = (link: string) => {
        const destination = parseDeeplink(link)?.destination;
        if (!destination) return link;
        const key = `deeplinkScreen_${destination}`;
        const label = translate(key);
        return label === key ? destination : label;
      };

      const toggleStatusBanner = async (banner) => {
        const deletedMessage = banner.isActive
          ? 'confirmInActive'
          : 'confirmActive';
        const result = await Swal.fire({
          title: translate('areYouSureQ'),
          icon: 'warning',
          text: translate(deletedMessage),
          showCancelButton: true,
          buttonsStyling: false,
          confirmButtonText: translate('confirm'),
          cancelButtonText: translate('cancel'),
          customClass: {
            confirmButton: 'btn fw-bold btn-warning',
            cancelButton: 'btn btn-light',
          },
        });

        if (result.value) {
          loading.value = true;
          await store.dispatch(Actions.DELETE_BANNER, banner.id);
          await store.dispatch(Actions.GET_BANNERS);
          loading.value = false;
          const [errorName] = Object.keys(store.getters.getErrors);
          const error = store.getters.getErrors[errorName];
          if (error) {
            Swal.fire({
              text: translate(error[0]),
              icon: 'error',
              buttonsStyling: false,
              confirmButtonText: translate('tryAgainExcl'),
              customClass: {
                confirmButton: 'btn fw-bold btn-light-danger',
              },
            });
          } else {
            const successMsg = banner.isActive
              ? 'SUCCESSFULLY_INACTIVATED'
              : 'SUCCESSFULLY_ACTIVEATED';
            Swal.fire({
              text: translate(successMsg),
              icon: 'success',
              buttonsStyling: false,
              confirmButtonText: translate('ok'),
              customClass: {
                confirmButton: 'btn btn-light-primary',
              },
            });
          }
        }
      };

      const goTo = (pageName) => {
        router.push({ name: pageName });
      };
      const editBanner = (id) => {
        router.push({ name: 'banner-editing', params: { id } });
      };

      const pageChanged = async (val) => {
        loading.value = true;
        await store.dispatch(Actions.UPDATE_BANNER_CURRENT_PAGE, val);
        await store.dispatch(Actions.GET_BANNERS);
        loading.value = false;
      };

      const onSubmitFilter = async () => {
        loading.value = true;
        await store.dispatch(Actions.GET_BANNERS, {
          search: search.value || '',
          screen: screen.value || undefined,
        });
        loading.value = false;
      };

      const onResetFilters = async (value) => {
        loading.value = true;
        await store.dispatch(Actions.GET_BANNERS);
        loading.value = false;
      };

      store.dispatch(Actions.REMOVE_BODY_CLASSNAME, 'page-loading-over');

      return {
        tableData: computed(() => store.getters.bannersList),
        totalItems: computed(() => store.getters.bannersListCount),
        tableHeader,
        deeplinkScreen,
        toggleStatusBanner,
        translate,
        pageChanged,
        goTo,
        editBanner,
        loading,
        can,
        onSubmitFilter,
        onResetFilters,
        search,
        screenOptions,
        screen,
        bannersAssets,
      };
    },
  });
